import { useMemo } from 'react'
import { Box } from '@mui/material'
import {
  Calendar,
  dateFnsLocalizer,
  type Event as RbcEvent,
  type EventPropGetter,
} from 'react-big-calendar'
import { format, parse, startOfWeek, getDay } from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { useNavigate } from 'react-router-dom'
import type { EventSummary } from '../../services/adminService'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS },
})

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  published: { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  draft: { color: '#B5611A', bg: 'rgba(228,119,32,0.15)' },
  archived: { color: '#707070', bg: 'rgba(112,112,112,0.12)' },
}

type CalendarEvent = RbcEvent & { resource: EventSummary }

export default function EventsCalendar({ events }: { events: EventSummary[] }) {
  const navigate = useNavigate()

  const calendarEvents = useMemo<CalendarEvent[]>(
    () =>
      events.map((ev) => ({
        title: ev.name,
        start: new Date(ev.startsAt),
        end: new Date(ev.endsAt),
        resource: ev,
      })),
    [events],
  )

  const eventPropGetter: EventPropGetter<CalendarEvent> = (event) => {
    const sc = STATUS_COLORS[event.resource.status] ?? STATUS_COLORS.draft
    return {
      style: {
        backgroundColor: sc.bg,
        color: sc.color,
        border: 'none',
        borderRadius: 6,
        fontWeight: 700,
        fontSize: '0.72rem',
      },
    }
  }

  return (
    <Box sx={{ height: 640 }}>
      <Calendar<CalendarEvent>
        localizer={localizer}
        events={calendarEvents}
        views={['month']}
        defaultView="month"
        startAccessor="start"
        endAccessor="end"
        popup
        eventPropGetter={eventPropGetter}
        onSelectEvent={(event) => navigate(`/admin/events/${event.resource.id}`)}
        style={{ height: '100%' }}
      />
    </Box>
  )
}
